import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuthStore } from '../../../stores/auth-store';
import { useUpdateExternalUser } from '../../../hooks/users/useExternalUsers';
import { FormInput } from '../../../shared/FormInput';
import { FormSelect } from '../../../shared/FormSelect';
import { EmailInput } from '../../../shared/EmailInput';
import { PhoneInput } from '../../../shared/PhoneInput';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose }) => {
  const { currentUser, updateCurrentUser } = useAuthStore();
  const updateUserMutation = useUpdateExternalUser();

  const [formData, setFormData] = useState({
    civility: '',
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    position: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (currentUser) {
      setFormData({
        civility: currentUser.civility || '',
        firstname: currentUser.firstname || '',
        lastname: currentUser.lastname || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        position: currentUser.position || '',
      });
    }
  }, [currentUser, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstname?.trim()) {
      newErrors.firstname = 'Le prénom est requis';
    }

    if (!formData.lastname?.trim()) {
      newErrors.lastname = 'Le nom est requis';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !currentUser?.id) {
      return;
    }

    try {
      await updateUserMutation.mutateAsync({
        id: currentUser.id,
        data: {
          civility: formData.civility || undefined,
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          phone: formData.phone || undefined,
          position: formData.position || undefined,
        },
      });

      updateCurrentUser({
        civility: formData.civility || undefined,
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: formData.phone || undefined,
        position: formData.position || undefined,
      });

      onClose();
    } catch (error: any) {
      setErrors({
        email: error.response?.data?.message || 'Erreur lors de la mise à jour',
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-mx-4 tw-max-h-[90vh] tw-overflow-y-auto">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
          <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">
            Modifier mes informations
          </h2>
          <button
            onClick={onClose}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="tw-p-6">
          <div className="tw-grid tw-grid-cols-12 tw-gap-4">
            {/* Ligne 1: Civilité (2) + Prénom (5) + Nom (5) */}
            <FormSelect
              label="Civilité"
              value={formData.civility}
              onChange={(value) => setFormData({ ...formData, civility: value })}
              options={[
                { value: '', label: 'Sélectionner' },
                { value: 'M.', label: 'M.' },
                { value: 'Mme', label: 'Mme' },
              ]}
              placeholder="Sélectionner"
              className="tw-col-span-2"
            />

            <FormInput
              label="Prénom"
              value={formData.firstname}
              onChange={(value) => setFormData({ ...formData, firstname: value })}
              required
              error={errors.firstname}
              className="tw-col-span-5"
            />

            <FormInput
              label="Nom"
              value={formData.lastname}
              onChange={(value) => setFormData({ ...formData, lastname: value })}
              required
              error={errors.lastname}
              className="tw-col-span-5"
            />

            {/* Ligne 2: Email (6) + Téléphone (6) */}
            <EmailInput
              label="Email"
              value={formData.email}
              onChange={(value) => setFormData({ ...formData, email: value })}
              required
              checkEndpoint="/api/v2/external-users/check-email"
              excludeId={currentUser?.id}
              externalError={errors.email}
              className="tw-col-span-6"
            />

            <PhoneInput
              label="Téléphone"
              value={formData.phone}
              onChange={(value) => setFormData({ ...formData, phone: value })}
              className="tw-col-span-6"
            />

            {/* Ligne 3: Poste (12) */}
            <FormInput
              label="Poste"
              value={formData.position}
              onChange={(value) => setFormData({ ...formData, position: value })}
              className="tw-col-span-12"
            />
          </div>

          {/* Footer */}
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-6 tw-pt-6 tw-border-t tw-border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={updateUserMutation.isLoading}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-md hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              {updateUserMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
